import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { User } from '../../interfaces/auth/user';
import { Estimator } from '../../interfaces/calendar/estimator';
import { Note } from '../../interfaces/notes/note';
import { Contract } from '../../interfaces/sales-management/contract';
import { ApiService } from '../api/api.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { IdbService } from '../idb/idb.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  // Sidenav mode observer
  private sidenavModeSubject = new BehaviorSubject('');
  currentMode = this.sidenavModeSubject.asObservable();

  // Current route observer
  private pathSubject = new BehaviorSubject('');
  currentPath = this.pathSubject.asObservable();

  // Uid observer
  private uidSubject = new BehaviorSubject(this.localStorage.get('uid'));
  currentUid = this.uidSubject.asObservable();

  // Calendar estimator observer
  private selectedEstimator = new BehaviorSubject({ username: '', userid: '' });
  currentEstimator = this.selectedEstimator.asObservable();

  // Calendar zone observer
  private selectedZone = new BehaviorSubject<number | null>(null);
  currentZone = this.selectedZone.asObservable();

  // Prod Calendar foreman observer
  private selectedForeman = new BehaviorSubject<User | null>(null);
  currentForeman = this.selectedForeman.asObservable();

  // Service calendar tech observer
  private selectedTech = new BehaviorSubject<User | null>(null);
  currentTech = this.selectedTech.asObservable();

  // Contract Observer
  private contractSubject = new BehaviorSubject<Contract | null>(null);
  currentContract = this.contractSubject.asObservable();

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private afs: AngularFirestore,
    private localStorage: LocalStorageService,
    private idb: IdbService,
    private apiService: ApiService
  ) {
    this.pathListener();
  }

  initUser(): Observable<string> {
    // console.log('initUser');
    return this.authService
      .userDetails()
      .pipe(
        switchMap((userData) => {
          // console.log('userData:', userData);
          if (userData) {
            return this.apiService
              .getUserData(userData.uid)
              .pipe(
                switchMap((userDoc) => {
                  // console.log('userDoc:', userDoc);
                  return this.idb.addUser(userDoc[0]);
                })
              );
          } else {
            throw new Error('No user data available');
          }
        })
      );
  }

  // Update the sidenav's modesubject
  changeMode(mode: string) {
    this.sidenavModeSubject.next(mode);
  }

  // Listen for path changes
  pathListener() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.changePath(event.url);
      }
    });
  }

  // Update the path subject
  changePath(url: string) {
    this.pathSubject.next(url);
  }

  // Update the UID subject/localStorage
  updateUid(uidValue: string) {
    this.localStorage.store('uid', uidValue);
  }

  // Save user data to localstroage
  saveUserData(userObject: User) {
    console.log('Saving user data to local storage');
    this.localStorage.store('uid', userObject.userId);
    this.localStorage.store('name', userObject.name);
    this.localStorage.store('userName', userObject.userName);
    this.localStorage.store('email', userObject.email);
    this.localStorage.store('employeeCode', userObject.employeeId);
    this.localStorage.store('role', userObject.userRoleName);
    // Can potentially add the rest later

    // Set Sentry Logging User and Tags
    this.setSentryContext(userObject);
  }

  setSentryContext(userObject: User) {
    console.log('Setting Sentry context', userObject);
    Sentry.setUser({
      UserId: userObject.userId,
      UserName: userObject.name,
      Email: userObject.email,
      UserRole: userObject.userRoleName,
    });

    Sentry.setTag('role', userObject.userRoleName);
    Sentry.setTag('companyCode', this.localStorage.get('companyCode'));
  }

  // Share estimator select from sidenav to calendar
  changeEstimator(estimator: Estimator | null) {
    if (estimator) {
      this.selectedEstimator.next(estimator);
    } else {
      this.selectedEstimator.next({ username: '', userid: '' });
    }
  }

  // Share zone select from sidenav to calendar
  changeZone(zoneId: number | null) {
    this.selectedZone.next(zoneId);
  }

  // Share foreman select from sidenav to calendar
  changeForeman(foreman: User | null) {
    if (foreman) {
      this.selectedForeman.next(foreman);
    } else {
      this.selectedForeman.next(null);
    }
  }

  // Share tech select from sidenav to calendar
  changeTech(tech: User | null) {
    if (tech) {
      this.selectedTech.next(tech);
    } else {
      this.selectedTech.next(null);
    }
  }

  updateContract(contract: Contract) {
    this.contractSubject.next(contract);
  }
}
